import { useState, useEffect, useRef } from 'react';
import { ReopenSetup } from '../components/settings/SetupChecklist';
import { StorageEmailTab } from '../components/settings/StorageEmailTab';
import { LetterheadPanel } from '../components/settings/LetterheadPanel';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Building2, Users, Key, Bell, CheckCircle2, Link2, RefreshCw, Upload, UsersRound, Mail } from 'lucide-react';
import { toast } from 'sonner';
import api, { xeroAPI, xpmAPI } from '../lib/api';
import { UsersTab } from '../components/settings/UsersTab';
import { SecurityTab } from '../components/settings/SecurityTab';
import { NotificationPrefs } from '../components/comms/NotificationPrefs';
import { MailboxSettings } from '../components/comms/MailboxSettings';
import { MessagesSquare, ShieldCheck } from 'lucide-react';
import { AtoConnectionWizard } from '../components/settings/AtoConnectionWizard';
import { EsignSettingsCard } from '../components/esign/EsignSettingsCard';
import { DspCompliance } from '../components/dsp/DspCompliance';
import { NominationSettings } from '../components/nomination/NominationSettings';
import { PortalSettingsTab } from '../components/settings/PortalSettingsTab';
import { IntegrationStatusCard } from '../components/settings/IntegrationStatusCard';
import { useNavigate } from 'react-router-dom';
import { AiSettings } from '../components/bas/AiSettings';

const NavTrigger = ({ value, children, testId }) => (
  <TabsTrigger value={value} data-testid={testId} className="shrink-0 justify-start whitespace-nowrap rounded-md px-3 py-1.5 text-sm data-[state=active]:bg-primary/10 data-[state=active]:text-primary data-[state=active]:shadow-none lg:w-full">{children}</TabsTrigger>
);
const NavGroup = ({ label, children }) => (
  <div className="flex shrink-0 items-center gap-0.5 lg:block lg:w-full lg:space-y-0.5"><p className="hidden px-3 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground lg:block">{label}</p>{children}</div>
);

export default function Settings() {
  const navigate = useNavigate();
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    if (sp.get('tab') === 'engagement-letters') {
      const t = { existing: 'send', templates: 'templates', clauses: 'clauses', offshore: 'offshore' }[sp.get('el')] || 'templates';
      navigate(`/client-engagements?tab=${t}`, { replace: true });
    }
  }, [navigate]);
  const [user, setUser] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [xeroStatus, setXeroStatus] = useState(null);
  const [xeroBusy, setXeroBusy] = useState(false);
  const [xpmStatus, setXpmStatus] = useState(null);
  const [xpmBusy, setXpmBusy] = useState(false);
  const [xpmSyncRun, setXpmSyncRun] = useState(null);
  const [xpmConfig, setXpmConfig] = useState(null);
  const [xpmCsvResult, setXpmCsvResult] = useState(null);
  const [xpmCsvBusy, setXpmCsvBusy] = useState(false);
  const [xeroTestResult, setXeroTestResult] = useState(null);
  const xpmCsvInputRef = useRef(null);
  const [activeTab, setActiveTab] = useState(() => { const t = new URLSearchParams(window.location.search).get('tab') || 'organization'; return t === 'email' ? 'storage-email' : t; });
  const [userRole, setUserRole] = useState(undefined);
  const [lhOpen, setLhOpen] = useState(false);
  const [firmDetails, setFirmDetails] = useState({ firm_abn: '', firm_address: '', firm_phone: '', firm_email: '', ipa_member_number: '', legal_name: '', trading_name: '', tax_agent_number: '', bas_agent_number: '', professional_body: '', cpa_member_number: '', ca_member_number: '' });
  const [firmCanManage, setFirmCanManage] = useState(false);
  const [firmSaving, setFirmSaving] = useState(false);
  // Only owner/admin (manage_firm) can manage users.
  const canManageUsers = ['owner', 'admin'].includes(userRole);

  useEffect(() => {
    loadSettings();
    loadXeroStatus();
    loadXpmStatus();
    loadFirmDetails();
  }, []);

  // Surface the result of the OAuth round-trip (callback redirects back with ?xero=... / ?xpm=...).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get('xero');
    const xpmResult = params.get('xpm');
    if (result === 'connected') {
      toast.success('Xero connected successfully');
      setActiveTab('integrations');
      loadXeroStatus();
    } else if (result === 'error') {
      toast.error('Xero connection failed. Please try again.');
      setActiveTab('integrations');
    }
    if (xpmResult === 'connected') {
      toast.success('Xero Practice Manager connected successfully');
      setActiveTab('integrations');
      loadXpmStatus();
    } else if (xpmResult === 'error') {
      toast.error(`XPM connection failed${params.get('reason') ? ` (${params.get('reason')})` : ''}. Please try again.`);
      setActiveTab('integrations');
    }
    if (result || xpmResult) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const loadSettings = async () => {
    try {
      const response = await api.get('/api/me');
      setUser(response.data.user);
      setOrganization(response.data.active_organization);
      setUserRole(response.data.active_membership?.role);
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const loadXeroStatus = async () => {
    try {
      const { data } = await xeroAPI.getStatus();
      setXeroStatus(data);
    } catch (error) {
      // Non-fatal: just leave status unknown.
      setXeroStatus({ connected: false });
    }
  };

  const loadFirmDetails = async () => {
    try {
      const { data } = await api.get('/api/organizations/firm-details');
      setFirmDetails({
        firm_abn: data.firm_abn || '', firm_address: data.firm_address || '',
        firm_phone: data.firm_phone || '', firm_email: data.firm_email || '',
        ipa_member_number: data.ipa_member_number || '',
        legal_name: data.legal_name || '',
        trading_name: data.trading_name || '',
        tax_agent_number: data.tax_agent_number || '',
        bas_agent_number: data.bas_agent_number || '',
        professional_body: data.professional_body || '',
        cpa_member_number: data.cpa_member_number || '',
        ca_member_number: data.ca_member_number || '',
      });
      setFirmCanManage(!!data.can_manage);
    } catch { /* non-fatal */ }
  };

  const saveFirmDetails = async () => {
    setFirmSaving(true);
    try {
      await api.put('/api/organizations/firm-details', firmDetails);
      toast.success('Firm details saved — they now appear on engagement letters');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save firm details');
    } finally {
      setFirmSaving(false);
    }
  };

  const handleXeroConnect = async () => {
    setXeroBusy(true);
    try {
      const { data } = await xeroAPI.connect();
      // Navigate the browser to Xero's login/consent page.
      window.location.href = data.authorize_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not start Xero connection');
      setXeroBusy(false);
    }
  };

  const handleXeroDisconnect = async () => {
    setXeroBusy(true);
    try {
      await xeroAPI.disconnect();
      toast.success('Xero disconnected');
      await loadXeroStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not disconnect Xero');
    } finally {
      setXeroBusy(false);
    }
  };

  const handleXeroTestCall = async () => {
    setXeroBusy(true);
    try {
      const { data } = await xeroAPI.testCall();
      setXeroTestResult(data);
      toast.success(`API call OK (${data.status_code}) — ${data.organisation_name}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Xero API call failed');
    } finally {
      setXeroBusy(false);
    }
  };

  const loadXpmStatus = async () => {
    try {
      const [{ data: status }, { data: sync }, { data: config }] = await Promise.all([
        xpmAPI.getStatus(),
        xpmAPI.getSyncStatus(),
        xpmAPI.getConfig(),
      ]);
      setXpmStatus(status);
      setXpmSyncRun(sync.run);
      setXpmConfig(config);
      if (sync.run?.status === 'running') pollXpmSync();
    } catch (error) {
      setXpmStatus({ connected: false });
    }
  };

  const pollXpmSync = () => {
    const timer = setInterval(async () => {
      try {
        const { data } = await xpmAPI.getSyncStatus();
        setXpmSyncRun(data.run);
        if (data.run?.status !== 'running') {
          clearInterval(timer);
          setXpmBusy(false);
          if (data.run?.status === 'completed') {
            const s = data.run.stats || {};
            toast.success(`XPM sync complete: ${s.created || 0} created, ${s.updated || 0} updated.`);
          } else if (data.run?.status === 'failed') {
            toast.error(`XPM sync failed: ${data.run.error || 'unknown error'}`);
          }
        }
      } catch {
        clearInterval(timer);
        setXpmBusy(false);
      }
    }, 2000);
  };

  const handleXpmConnect = async () => {
    setXpmBusy(true);
    try {
      const { data } = await xpmAPI.connect();
      window.location.href = data.authorize_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not start XPM connection');
      setXpmBusy(false);
    }
  };

  const handleXpmSync = async () => {
    setXpmBusy(true);
    toast.info('Syncing clients from Xero Practice Manager…');
    try {
      await xpmAPI.syncClients();
      pollXpmSync();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not start XPM sync');
      setXpmBusy(false);
    }
  };

  const handleXpmCsvUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    setXpmCsvBusy(true);
    setXpmCsvResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await xpmAPI.importCsv(formData);
      setXpmCsvResult(data);
      toast.success(`CSV imported: ${data.created} created, ${data.updated} updated${data.skipped ? `, ${data.skipped} skipped` : ''}.`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'CSV import failed');
    } finally {
      setXpmCsvBusy(false);
    }
  };

  const handleXpmDisconnect = async () => {
    setXpmBusy(true);
    try {
      await xpmAPI.disconnect();
      toast.success('Xero Practice Manager disconnected');
      await loadXpmStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not disconnect XPM');
    } finally {
      setXpmBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/4"></div>
          <div className="h-64 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 sm:p-6 lg:p-8">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-foreground sm:text-3xl" data-testid="settings-title">
          Settings
        </h1>
        <p className="text-muted-foreground mt-1">Manage your practice, team and connections</p>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); const u = new URL(window.location.href); u.searchParams.set('tab', v); window.history.replaceState(null, '', u); }} orientation="vertical" className="grid w-full gap-6 lg:grid-cols-[220px_minmax(0,1fr)]">
        <TabsList className="flex h-auto max-w-full flex-row flex-nowrap items-start justify-start gap-1 overflow-x-auto bg-transparent p-0 lg:sticky lg:top-20 lg:flex-col lg:overflow-visible" data-testid="settings-nav">
          <NavGroup label="Practice">
            <NavTrigger value="organization">Practice profile</NavTrigger>
            {canManageUsers && <NavTrigger value="client-onboarding" testId="client-onboarding-settings-tab">Client onboarding</NavTrigger>}
            {canManageUsers && <NavTrigger value="client-portal" testId="client-portal-settings-tab">Client portal</NavTrigger>}
          </NavGroup>
          <NavGroup label="Team & roles">
            <NavTrigger value="account">My account</NavTrigger>
            {canManageUsers && <NavTrigger value="users" testId="users-tab">Users &amp; roles</NavTrigger>}
          </NavGroup>
          {/* Engagement Letters moved to Client engagements (old ?tab=engagement-letters links redirect). */}
          <NavGroup label="Integrations">
            <NavTrigger value="integrations">Connections</NavTrigger>
            {canManageUsers && <NavTrigger value="storage-email" testId="storage-email-tab">Storage &amp; Email</NavTrigger>}
          </NavGroup>
          <NavGroup label="AI"><NavTrigger value="ai" testId="ai-settings-tab">AI suggestions</NavTrigger></NavGroup>
          <NavGroup label="Security">
            <NavTrigger value="security" testId="security-tab">Security</NavTrigger>
            {canManageUsers && <NavTrigger value="dsp" testId="dsp-compliance-tab">DSP compliance</NavTrigger>}
          </NavGroup>
          <NavGroup label="Notifications"><NavTrigger value="communications" testId="communications-settings-tab">Communications &amp; notifications</NavTrigger></NavGroup>
        </TabsList>
        <div className="min-w-0 space-y-6">
        <TabsContent value="ai" className="mt-0 space-y-6" data-testid="settings-ai">
          <p className="text-sm text-muted-foreground">Claude BAS suggestions are the only AI feature. Everything else in Practice Suite is rule-based. <a href="/agents" className="text-primary underline">See AI &amp; Automation</a>.</p>
          <AiSettings role={userRole} />
        </TabsContent>
        {canManageUsers && <TabsContent value="client-portal" className="space-y-6"><PortalSettingsTab /></TabsContent>}
        {/* engagement-letters tab moved to /client-engagements */}
        <TabsContent value="client-onboarding" className="space-y-6">
          <Card><CardHeader><CardTitle>ATO Agent Nomination</CardTitle>
            <CardDescription>Client-to-agent nomination wizard settings for this practice. Nomination never blocks onboarding completion.</CardDescription></CardHeader>
            <CardContent><NominationSettings canEdit={canManageUsers} /></CardContent></Card>
        </TabsContent>

        <TabsContent value="communications" className="space-y-6" data-testid="communications-settings">
          <Card>
            <CardHeader>
              <CardTitle>Communications</CardTitle>
              <CardDescription>Choose how you're notified about client messages, assignments, @mentions and follow-ups.</CardDescription>
            </CardHeader>
            <CardContent><NotificationPrefs /></CardContent>
          </Card>
          {canManageUsers && (
            <Card data-testid="mailboxes-settings-card">
              <CardHeader>
                <CardTitle>Mailboxes</CardTitle>
                <CardDescription>Connect Microsoft 365 or Gmail mailboxes so client email syncs into Communications and staff can reply from the portal.</CardDescription>
              </CardHeader>
              <CardContent><MailboxSettings /></CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="organization" className="space-y-6">
          {canManageUsers && <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setLhOpen(true)} data-testid="settings-open-letterhead">Logo &amp; letterhead</Button>
            <ReopenSetup />
          </div>}
          <LetterheadPanel open={lhOpen} onOpenChange={setLhOpen} canEdit={canManageUsers} />
          <Card>
            <CardHeader>
              <CardTitle style={{}}>Organization Profile</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Organization Name</Label>
                <p className="text-lg font-medium text-foreground">{organization?.name}</p>
              </div>

              <div className="space-y-2">
                <Label>Organization ID</Label>
                <p className="text-sm font-mono text-muted-foreground">{organization?.id}</p>
              </div>

              <div className="space-y-2">
                <Label>Slug</Label>
                <p className="text-sm text-muted-foreground">{organization?.slug}</p>
              </div>

              <div className="pt-4 border-t">
                <p className="text-sm text-muted-foreground">
                  To update organization details, contact your administrator.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle style={{}}>Firm Letterhead Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                These details appear on your engagement letters (ABN, address and IPA membership).
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Firm ABN</Label>
                  <Input value={firmDetails.firm_abn} disabled={!firmCanManage} className="font-mono"
                    onChange={(e) => setFirmDetails((f) => ({ ...f, firm_abn: e.target.value }))}
                    data-testid="firm-abn-input" placeholder="e.g. 12 345 678 901" />
                </div>
                <div className="space-y-2">
                  <Label>IPA Member Number</Label>
                  <Input value={firmDetails.ipa_member_number} disabled={!firmCanManage} className="font-mono"
                    onChange={(e) => setFirmDetails((f) => ({ ...f, ipa_member_number: e.target.value }))}
                    data-testid="firm-ipa-input" placeholder="Optional" />
                </div>
                <div className="space-y-2">
                  <Label>Legal name</Label>
                  <Input value={firmDetails.legal_name} disabled={!firmCanManage}
                    onChange={(e) => setFirmDetails((f) => ({ ...f, legal_name: e.target.value }))}
                    data-testid="firm-legal-name-input" placeholder="Optional" />
                </div>
                <div className="space-y-2">
                  <Label>Trading name</Label>
                  <Input value={firmDetails.trading_name} disabled={!firmCanManage}
                    onChange={(e) => setFirmDetails((f) => ({ ...f, trading_name: e.target.value }))}
                    data-testid="firm-trading-name-input" placeholder="Optional" />
                </div>
                <div className="space-y-2">
                  <Label>Tax agent number</Label>
                  <Input value={firmDetails.tax_agent_number} disabled={!firmCanManage}
                    onChange={(e) => setFirmDetails((f) => ({ ...f, tax_agent_number: e.target.value }))}
                    data-testid="firm-tax-agent-number-input" placeholder="e.g. 12345678" />
                </div>
                <div className="space-y-2">
                  <Label>BAS agent number</Label>
                  <Input value={firmDetails.bas_agent_number} disabled={!firmCanManage}
                    onChange={(e) => setFirmDetails((f) => ({ ...f, bas_agent_number: e.target.value }))}
                    data-testid="firm-bas-agent-number-input" placeholder="Optional" />
                </div>
                <div className="space-y-2">
                  <Label>Professional body</Label>
                  <Input value={firmDetails.professional_body} disabled={!firmCanManage}
                    onChange={(e) => setFirmDetails((f) => ({ ...f, professional_body: e.target.value }))}
                    data-testid="firm-professional-body-input" placeholder="IPA / CPA / CA" />
                </div>
                <div className="space-y-2">
                  <Label>CPA member number</Label>
                  <Input value={firmDetails.cpa_member_number} disabled={!firmCanManage}
                    onChange={(e) => setFirmDetails((f) => ({ ...f, cpa_member_number: e.target.value }))}
                    data-testid="firm-cpa-member-number-input" placeholder="Optional" />
                </div>
                <div className="space-y-2">
                  <Label>CA member number</Label>
                  <Input value={firmDetails.ca_member_number} disabled={!firmCanManage}
                    onChange={(e) => setFirmDetails((f) => ({ ...f, ca_member_number: e.target.value }))}
                    data-testid="firm-ca-member-number-input" placeholder="Optional" />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input value={firmDetails.firm_phone} disabled={!firmCanManage}
                    onChange={(e) => setFirmDetails((f) => ({ ...f, firm_phone: e.target.value }))}
                    data-testid="firm-phone-input" />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input value={firmDetails.firm_email} disabled={!firmCanManage}
                    onChange={(e) => setFirmDetails((f) => ({ ...f, firm_email: e.target.value }))}
                    data-testid="firm-email-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Firm Address</Label>
                <Textarea rows={2} value={firmDetails.firm_address} disabled={!firmCanManage}
                  onChange={(e) => setFirmDetails((f) => ({ ...f, firm_address: e.target.value }))}
                  data-testid="firm-address-input" placeholder="Street, Suburb, State, Postcode" />
              </div>
              {firmCanManage && (
                <div className="flex justify-end">
                  <Button onClick={saveFirmDetails} disabled={firmSaving} data-testid="firm-details-save">
                    {firmSaving ? 'Saving…' : 'Save Firm Details'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="account" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle style={{}}>Account Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Name</Label>
                <p className="text-lg font-medium text-foreground">{user?.name}</p>
              </div>

              <div className="space-y-2">
                <Label>Email</Label>
                <p className="text-sm text-muted-foreground">{user?.email}</p>
              </div>

              <div className="space-y-2">
                <Label>User ID</Label>
                <p className="text-sm font-mono text-muted-foreground">{user?.id}</p>
              </div>

              <div className="pt-4 border-t">
                <p className="text-sm text-muted-foreground">
                  Password and email changes coming soon.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {canManageUsers && (
          <TabsContent value="dsp" className="space-y-6">
            <DspCompliance />
          </TabsContent>
        )}

        <TabsContent value="integrations" className="space-y-6">
          <IntegrationStatusCard xeroStatus={xeroStatus} xpmStatus={xpmStatus} />
          {canManageUsers && <AtoConnectionWizard />}
          {canManageUsers && <EsignSettingsCard />}
          {/* Xero Practice Manager (XPM 3.1) — client sync */}
          <Card>
            <CardHeader>
              <CardTitle style={{}}>
                Xero Practice Manager
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between gap-4 p-4 rounded-lg border border-border bg-card">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Link2 className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium text-foreground" data-testid="xpm-status-text">
                      {xpmStatus?.connected ? 'Connected' : 'Not connected'}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {xpmStatus?.connected
                        ? `Practice: ${xpmStatus.connection?.xero_tenant_name || '—'}`
                        : 'Pull your XPM client list directly into the Clients module (XPM API 3.1).'}
                    </p>
                  </div>
                </div>

                {xpmStatus?.connected ? (
                  <div className="flex items-center gap-3">
                    <span className="inline-flex items-center gap-1 text-sm text-green-600">
                      <CheckCircle2 className="w-4 h-4" /> Active
                    </span>
                    <Button
                      onClick={handleXpmSync}
                      disabled={xpmBusy || xpmSyncRun?.status === 'running'}
                      data-testid="xpm-sync-btn"
                    >
                      <RefreshCw className={`w-4 h-4 mr-2 ${(xpmBusy || xpmSyncRun?.status === 'running') ? 'animate-spin' : ''}`} />
                      {xpmSyncRun?.status === 'running' ? 'Syncing…' : 'Sync Clients'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleXpmDisconnect}
                      disabled={xpmBusy}
                      data-testid="xpm-disconnect-btn"
                    >
                      Disconnect
                    </Button>
                  </div>
                ) : (
                  <Button
                    onClick={handleXpmConnect}
                    disabled={xpmBusy}
                    data-testid="xpm-connect-btn"
                  >
                    {xpmBusy ? 'Connecting…' : 'Connect XPM'}
                  </Button>
                )}
              </div>

              {!xpmStatus?.connected && (
                <div className="p-4 rounded-lg border border-amber-200 dark:border-amber-800/40 bg-amber-50 dark:bg-amber-950/30 space-y-3" data-testid="xpm-setup-help">
                  <p className="text-sm font-semibold text-amber-900 dark:text-amber-300">
                    Before connecting — Xero-side setup required
                  </p>
                  <p className="text-xs text-amber-900/80 dark:text-amber-200/80">
                    If Xero shows <span className="font-data">"Error: invalid_scope"</span> during connect, your Xero app
                    has not yet been granted Practice Manager API access. XPM access is gated by Xero:
                  </p>
                  <ol className="text-xs text-amber-900/80 dark:text-amber-200/80 space-y-1.5 list-decimal list-inside">
                    <li>
                      Request XPM API access for your app — complete Xero's security self-assessment and the
                      XPM API Access Form (or email <span className="font-data">api@support.xero.com</span>) so the
                      <span className="font-data"> {xpmConfig?.scopes?.includes('practicemanager') ? 'practicemanager' : ''}</span> scope
                      is enabled for app <span className="font-data">{xpmConfig?.client_id_preview || '—'}</span>.
                    </li>
                    <li>
                      In your Xero app's configuration, add this exact redirect URI:
                      <span className="block mt-1 font-data text-[11px] bg-card border border-border rounded px-2 py-1 break-all select-all" data-testid="xpm-redirect-uri">
                        {xpmConfig?.redirect_uri || '—'}
                      </span>
                    </li>
                    <li>
                      In XPM, enable <em>"Connect third-party add-ons"</em> for your staff login
                      (Business &gt; Settings &gt; Staff).
                    </li>
                  </ol>
                  <p className="text-xs text-amber-900/70 dark:text-amber-200/70">
                    Requested scopes: <span className="font-data">{xpmConfig?.scopes || '—'}</span>
                    {!xpmConfig?.app_configured && (
                      <span className="block mt-1 font-medium text-destructive">Xero app credentials are not configured on the server.</span>
                    )}
                  </p>
                </div>
              )}

              {xpmSyncRun && (
                <div className="text-xs text-muted-foreground/80 font-data" data-testid="xpm-sync-summary">
                  {xpmSyncRun.status === 'running' && (
                    <>Sync in progress — {xpmSyncRun.stats?.total || 0} clients processed so far…</>
                  )}
                  {xpmSyncRun.status === 'completed' && (
                    <>Last {xpmSyncRun.type === 'csv_import' ? 'CSV import' : 'sync'}: {xpmSyncRun.stats?.created || 0} created, {xpmSyncRun.stats?.updated || 0} updated ({xpmSyncRun.stats?.total || 0} total) · {xpmSyncRun.finished_at}</>
                  )}
                  {xpmSyncRun.status === 'failed' && (
                    <span className="text-destructive">Last sync failed: {xpmSyncRun.error}</span>
                  )}
                </div>
              )}

              {/* Manual CSV import — works without API access */}
              <div className="p-4 rounded-lg border border-dashed border-border bg-muted/30">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium text-foreground">Import client list from CSV</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      No API access yet? In XPM go to Business &gt; Clients &gt; Export, then upload the CSV here.
                      Clients are matched by UUID, client code or name — safe to re-upload.
                    </p>
                  </div>
                  <input
                    type="file"
                    accept=".csv,text/csv"
                    ref={xpmCsvInputRef}
                    onChange={handleXpmCsvUpload}
                    className="hidden"
                    data-testid="xpm-csv-input"
                  />
                  <Button
                    variant="outline"
                    onClick={() => xpmCsvInputRef.current?.click()}
                    disabled={xpmCsvBusy}
                    data-testid="xpm-csv-upload-btn"
                  >
                    <Upload className={`w-4 h-4 mr-2 ${xpmCsvBusy ? 'animate-pulse' : ''}`} />
                    {xpmCsvBusy ? 'Importing…' : 'Upload CSV'}
                  </Button>
                </div>
                {xpmCsvResult && (
                  <div className="mt-3 text-xs font-data text-muted-foreground" data-testid="xpm-csv-result">
                    Imported {xpmCsvResult.total} rows — {xpmCsvResult.created} created, {xpmCsvResult.updated} updated, {xpmCsvResult.skipped} skipped.
                    {xpmCsvResult.errors?.length > 0 && (
                      <ul className="mt-1 text-destructive list-disc list-inside">
                        {xpmCsvResult.errors.slice(0, 5).map((er, i) => (
                          <li key={i}>Row {er.row}: {er.reason}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Xero (Accounting) — contacts sync */}
          <Card>
            <CardHeader>
              <CardTitle style={{}}>
                Xero
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between gap-4 p-4 rounded-lg border border-border bg-card">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#13B5EA]/10 flex items-center justify-center">
                    <Link2 className="w-5 h-5 text-[#13B5EA]" />
                  </div>
                  <div>
                    <p className="font-medium text-foreground">
                      {xeroStatus?.connected ? 'Connected' : 'Not connected'}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {xeroStatus?.connected
                        ? `Organisation: ${xeroStatus.tenant_name || '—'}`
                        : 'Accounting connection (financials). Client list sync runs exclusively through Xero Practice Manager above.'}
                    </p>
                  </div>
                </div>

                {xeroStatus?.connected ? (
                  <div className="flex items-center gap-3">
                    <span className="inline-flex items-center gap-1 text-sm text-green-600">
                      <CheckCircle2 className="w-4 h-4" /> Active
                    </span>
                    <Button
                      onClick={handleXeroTestCall}
                      disabled={xeroBusy}
                      data-testid="xero-test-call-btn"
                    >
                      <RefreshCw className={`w-4 h-4 mr-2 ${xeroBusy ? 'animate-spin' : ''}`} />
                      {xeroBusy ? 'Calling…' : 'Test API Call'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleXeroDisconnect}
                      disabled={xeroBusy}
                      data-testid="xero-disconnect-btn"
                    >
                      Disconnect
                    </Button>
                  </div>
                ) : (
                  <Button
                    onClick={handleXeroConnect}
                    disabled={xeroBusy}
                    className="bg-[#13B5EA] hover:bg-[#0f9fce] text-white"
                    data-testid="xero-connect-btn"
                  >
                    {xeroBusy ? 'Connecting…' : 'Connect Xero'}
                  </Button>
                )}
              </div>

              {/* Successful-API-call evidence (Xero internal-use access, step 2) */}
              {(xeroTestResult || xeroStatus?.last_test_call) && (
                <div className="p-3 rounded-lg border border-green-200 dark:border-green-800/40 bg-green-50 dark:bg-green-950/30 text-xs font-data text-green-800 dark:text-green-300" data-testid="xero-test-result">
                  ✓ {(xeroTestResult || xeroStatus.last_test_call).called_endpoint} → HTTP {(xeroTestResult || xeroStatus.last_test_call).status_code} ·
                  Organisation: {(xeroTestResult || xeroStatus.last_test_call).organisation_name} ·
                  {(xeroTestResult || xeroStatus.last_test_call).called_at}
                </div>
              )}

              {!xeroStatus?.connected && (
                <div className="p-4 rounded-lg border border-border bg-muted/30 text-xs text-muted-foreground space-y-1.5" data-testid="xero-setup-help">
                  <p className="font-semibold text-foreground">For the Xero internal-use prerequisite (step 2):</p>
                  <p>1. In your Xero app's configuration, add this redirect URI:</p>
                  <p className="font-data text-[11px] bg-card border border-border rounded px-2 py-1 break-all select-all">
                    {`${process.env.REACT_APP_BACKEND_URL}/api/integrations/xero/callback`}
                  </p>
                  <p>2. Click <strong>Connect Xero</strong> and choose the <strong>Demo Company</strong> on the consent screen (standard scopes only — no XPM approval needed).</p>
                  <p>3. Press <strong>Test API Call</strong> — a successful GET Organisation call is the evidence Xero asks for before the security assessment.</p>
                </div>
              )}

              {xeroStatus?.connected && xeroStatus?.last_inbound_poll_at && (
                <p className="text-xs text-muted-foreground/70">
                  Last synced from Xero: {xeroStatus.last_inbound_poll_at}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Other integrations: honest summary lives at the top of this tab */}
        </TabsContent>

        <TabsContent value="security" className="space-y-6">
          <SecurityTab />
        </TabsContent>

        {canManageUsers && (
          <TabsContent value="users" className="space-y-6">
            <UsersTab />
          </TabsContent>
        )}

        {canManageUsers && (
          <TabsContent value="storage-email" className="space-y-6"><StorageEmailTab /></TabsContent>
        )}
        {!canManageUsers && ['storage-email', 'email'].includes(activeTab) && (
          <p className="text-sm text-muted-foreground" data-testid="storage-email-no-access">Only practice owners and admins can manage storage and email.</p>
        )}
        </div>
      </Tabs>
    </div>
  );
}
